import { useEffect, useState, useRef } from "react";
import { Package, Save, X, DollarSign, Boxes, AlertCircle, Tag, Ruler, ShoppingCart, Plus, Trash2, Image } from "lucide-react";
import Swal from "sweetalert2";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";

// Catálogo de Hacienda CAT-014 Unidad de Medida
const UNIDADES_MEDIDA = [
  { codigo: 1, nombre: "metro" },
  { codigo: 2, nombre: "Yarda" },
  { codigo: 6, nombre: "milímetro" },
  { codigo: 9, nombre: "kilómetro cuadrado" },
  { codigo: 10, nombre: "Hectárea" },
  { codigo: 13, nombre: "metro cuadrado" },
  { codigo: 15, nombre: "Vara cuadrada" },
  { codigo: 18, nombre: "metro cúbico" },
  { codigo: 20, nombre: "Barril" },
  { codigo: 22, nombre: "Galón" },
  { codigo: 23, nombre: "Litro" },
  { codigo: 24, nombre: "Botella" },
  { codigo: 26, nombre: "Mililitro" },
  { codigo: 30, nombre: "Tonelada" },
  { codigo: 32, nombre: "Quintal" },
  { codigo: 33, nombre: "Arroba" },
  { codigo: 34, nombre: "Kilogramo" },
  { codigo: 36, nombre: "Libra" },
  { codigo: 37, nombre: "Onza troy" },
  { codigo: 38, nombre: "Onza" },
  { codigo: 39, nombre: "Gramo" },
  { codigo: 40, nombre: "Miligramo" },
  { codigo: 42, nombre: "Megawatt" },
  { codigo: 43, nombre: "Kilowatt" },
  { codigo: 44, nombre: "Watt" },
  { codigo: 45, nombre: "Megavoltio-amperio" },
  { codigo: 46, nombre: "Kilovoltio-amperio" },
  { codigo: 47, nombre: "Voltio-amperio" },
  { codigo: 49, nombre: "Gigawatt-hora" },
  { codigo: 50, nombre: "Megawatt-hora" },
  { codigo: 51, nombre: "Kilowatt-hora" },
  { codigo: 52, nombre: "Watt-hora" },
  { codigo: 53, nombre: "Kilovoltio" },
  { codigo: 54, nombre: "Voltio" },
  { codigo: 55, nombre: "Millar" },
  { codigo: 56, nombre: "Medio millar" },
  { codigo: 57, nombre: "Ciento" },
  { codigo: 58, nombre: "Docena" },
  { codigo: 59, nombre: "Unidad" },
  { codigo: 99, nombre: "Otra" },
];

// Categorías restaurante
const CATEGORIAS_RESTAURANTE = [
  { value: "comida", label: "🍽️ Comida" },
  { value: "bebida", label: "🥤 Bebida" },
  { value: "postre", label: "🍰 Postre" },
  { value: "otro",   label: "📦 Otro" },
];

// Categorías comedor
const CATEGORIAS_COMEDOR = [
  { value: "plato",           label: "🍽️ Plato" },
  { value: "pollo",           label: "🍗 Pollo" },
  { value: "mariscos",        label: "🦐 Mariscos" },
  { value: "carnes",          label: "🥩 Carnes" },
  { value: "sopas",           label: "🍜 Sopas" },
  { value: "especiales",      label: "⭐ Especiales" },
  { value: "acompañamientos", label: "🍚 Acompañamientos" },
  { value: "bebida",          label: "🥤 Bebidas" },
];

export default function ProductForm({ onSaved, editingProduct, onCancel }) {
  const codigoRef = useRef(null);

  const { user } = useAuth();
const categorias = user?.tipo_negocio === 'comedor' ? CATEGORIAS_COMEDOR : CATEGORIAS_RESTAURANTE;
const categoriaDefault = user?.tipo_negocio === 'comedor' ? 'plato' : 'comida';
  // 🆕 Ref para input de foto
  const fotoInputRef = useRef(null);

  const [codigo, setCodigo] = useState("");
  const [nombre, setNombre] = useState("");
  
  const [precioVenta, setPrecioVenta] = useState("");
  const [precioCompra, setPrecioCompra] = useState("");
  
  const [stock, setStock] = useState("");
  const [stockMinimo, setStockMinimo] = useState(5);
  const [descripcion, setDescripcion] = useState("");
  const [exentoIva, setExentoIva] = useState(false);
  
  // Campos fiscales
  const [tipoItem, setTipoItem] = useState(2); // 1=Bien, 2=Servicio
  const [unidadMedida, setUnidadMedida] = useState(59); // 59=Unidad

  // 🆕 Campos restaurante
  const [categoria, setCategoria] = useState(categoriaDefault);
  const [fotoFile, setFotoFile] = useState(null);       // archivo nuevo a subir
  const [fotoPreview, setFotoPreview] = useState(null); // URL preview local o existente
  const [quitarFotoExistente, setQuitarFotoExistente] = useState(false);

  // Estados para presentaciones
  const [tienePresentaciones, setTienePresentaciones] = useState(false);
  const [tipoPresentacion, setTipoPresentacion] = useState("empaque"); // 🆕 NUEVO: 'empaque' o 'variante'
  const [presentations, setPresentations] = useState([]);

  useEffect(() => {
    if (editingProduct) {
      setCodigo(editingProduct.codigo || "");
      setNombre(editingProduct.nombre || "");
      
      setPrecioVenta(editingProduct.precio_venta || "");
      setPrecioCompra(editingProduct.precio_compra || "");
      
      setStock(editingProduct.stock ?? "");
      setStockMinimo(editingProduct.stock_minimo ?? 5);
      setDescripcion(editingProduct.descripcion || "");
      setExentoIva(!editingProduct.aplica_iva);
      setTipoItem(editingProduct.tipo_item || 1);
      setUnidadMedida(editingProduct.unidad_medida || 59);

      // 🆕 Cargar campos restaurante
      setCategoria(editingProduct.categoria || "comida");
      setFotoFile(null);
      setFotoPreview(editingProduct.foto ? `https://restaurante.altivaweb.com/BACK/public/storage/${editingProduct.foto}` : null);
      setQuitarFotoExistente(false);

      // Cargar presentaciones desde la API
      const cargarPresentaciones = async () => {
        try {
          const response = await api.get(`/products/${editingProduct.id}`);
          const productData = response.data;
          
          if (productData.presentations && productData.presentations.length > 0) {
            setTienePresentaciones(true);
            
            // 🆕 Detectar el tipo de presentación basado en la primera presentación
            const primeraPresentacion = productData.presentations[0];
            setTipoPresentacion(primeraPresentacion.tipo_presentacion || 'empaque');
            
            setPresentations(productData.presentations.map(p => ({
              id: p.id,
              descripcion: p.descripcion,
              precio_compra: p.precio_compra || "", // 🆕 NUEVO
              precio_venta: p.precio_venta,
              factor_conversion: p.factor_conversion,
              tipo_presentacion: p.tipo_presentacion || 'empaque', // 🆕 NUEVO
              stock_independiente: p.stock_independiente || "" // 🆕 NUEVO
            })));
          } else {
            setTienePresentaciones(false);
            setPresentations([]);
          }
        } catch (error) {
          console.error('Error cargando presentaciones:', error);
          // Si falla, intentar con los datos del producto
          if (editingProduct.presentations && editingProduct.presentations.length > 0) {
            setTienePresentaciones(true);
            setPresentations(editingProduct.presentations);
          }
        }
      };

      if (editingProduct.tipo_item === 1) {
        cargarPresentaciones();
      }
    } else {
      resetForm();
    }

    setTimeout(() => {
      codigoRef.current?.focus();
    }, 100);
  }, [editingProduct]);

  const resetForm = () => {
    setCodigo("");
    setNombre("");
    setPrecioVenta("");
    setPrecioCompra("");
    setStock("");
    setStockMinimo(5);
    setDescripcion("");
    setExentoIva(false);
    setTipoItem(2);
    setUnidadMedida(59);
    
    // 🆕 Limpiar campos restaurante
    setCategoria(categoriaDefault);
    setFotoFile(null);
    setFotoPreview(null);
    setQuitarFotoExistente(false);
    if (fotoInputRef.current) fotoInputRef.current.value = "";

    setTienePresentaciones(false);
    setTipoPresentacion("empaque"); // 🆕 NUEVO
    setPresentations([]);

    setTimeout(() => {
      codigoRef.current?.focus();
    }, 50);
  };

  // 🆕 Manejar selección de foto
  const handleFotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      Swal.fire("Error", "Solo se permiten archivos de imagen (JPG, PNG, etc.)", "error");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      Swal.fire("Error", "La imagen no debe superar 2MB", "error");
      return;
    }
    setFotoFile(file);
    setFotoPreview(URL.createObjectURL(file));
    setQuitarFotoExistente(false);
  };

  // 🆕 Quitar foto
  const quitarFoto = () => {
    setFotoFile(null);
    setFotoPreview(null);
    setQuitarFotoExistente(true);
    if (fotoInputRef.current) fotoInputRef.current.value = "";
  };

  // Agregar presentación
  const agregarPresentacion = () => {
    setPresentations([
      ...presentations,
      {
        id: null,
        descripcion: "",
        precio_compra: tipoPresentacion === 'variante' ? "" : null, // 🆕 NUEVO
        precio_venta: "",
        factor_conversion: tipoPresentacion === 'empaque' ? 1 : 1,
        tipo_presentacion: tipoPresentacion,
        stock_independiente: tipoPresentacion === 'variante' ? "" : null
      }
    ]);
  };

  // Eliminar presentación
  const eliminarPresentacion = (index) => {
    setPresentations(presentations.filter((_, i) => i !== index));
  };

  // Actualizar campo de presentación
  const actualizarPresentacion = (index, campo, valor) => {
    const nuevasPresentaciones = [...presentations];
    nuevasPresentaciones[index][campo] = valor;
    setPresentations(nuevasPresentaciones);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

// Solo validar espacios si se ingresó algo
if (codigo.trim() && /\s/.test(codigo)) {
  Swal.fire("Error", "El código no debe contener espacios.", "error");
  return;
}

    // Validación: precio_compra obligatorio para Bienes
    if (parseInt(tipoItem) === 1 && !tienePresentaciones && !precioCompra) {
      Swal.fire("Error", "El precio de compra es obligatorio para productos tipo Bien.", "error");
    return;
    }

    // Validaciones para presentaciones
    if (tienePresentaciones && presentations.length === 0) {
      Swal.fire("Error", "Debe agregar al menos una presentación o desmarcar la opción de presentaciones múltiples.", "error");
      return;
    }

    if (tienePresentaciones) {
      // Validar que todas las presentaciones estén completas
      for (let i = 0; i < presentations.length; i++) {
        const pres = presentations[i];
        
        if (!pres.descripcion.trim()) {
          Swal.fire("Error", `La presentación ${i + 1} necesita una descripción.`, "error");
          return;
        }
        
        // 🆕 Validar precio de compra para variantes
        if (tipoPresentacion === 'variante') {
          if (!pres.precio_compra || parseFloat(pres.precio_compra) < 0) {
            Swal.fire("Error", `La presentación ${i + 1} necesita un precio de compra válido.`, "error");
            return;
          }
        }
        
        if (!pres.precio_venta || parseFloat(pres.precio_venta) <= 0) {
          Swal.fire("Error", `La presentación ${i + 1} necesita un precio válido.`, "error");
          return;
        }
        
        // 🆕 Validaciones según el tipo de presentación
        if (tipoPresentacion === 'empaque') {
          if (!pres.factor_conversion || parseInt(pres.factor_conversion) < 1) {
            Swal.fire("Error", `La presentación ${i + 1} necesita un factor de conversión válido.`, "error");
            return;
          }
        } else if (tipoPresentacion === 'variante') {
          // Para variantes, validar stock independiente
          if (pres.stock_independiente === "" || parseInt(pres.stock_independiente) < 0) {
            Swal.fire("Error", `La presentación ${i + 1} necesita un stock válido.`, "error");
            return;
          }
        }
      }
    }

    // Advertencia si precio_venta < precio_compra (solo si NO tiene presentaciones)
    if (!tienePresentaciones && parseInt(tipoItem) === 1 && precioCompra && precioVenta && parseFloat(precioVenta) < parseFloat(precioCompra)) {
      const result = await Swal.fire({
        title: "⚠️ Advertencia",
        text: "El precio de venta es menor que el precio de compra. Esto generará pérdidas. ¿Desea continuar?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, continuar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#f59e0b",
      });

      if (!result.isConfirmed) {
        return;
      }
    }

    // 🆕 Usar FormData para poder enviar la foto
    const formData = new FormData();
    formData.append("codigo", codigo.trim() || "");
    formData.append("nombre", nombre);
    formData.append("precio_venta", tienePresentaciones ? 0 : precioVenta);
    formData.append("precio_compra", parseInt(tipoItem) === 2 ? "" : (precioCompra || ""));
    formData.append("stock", parseInt(tipoItem) === 2 ? 0 : (tipoPresentacion === 'variante' && tienePresentaciones ? 0 : stock));
    formData.append("stock_minimo", parseInt(tipoItem) === 2 ? 0 : stockMinimo);
    formData.append("aplica_iva", !exentoIva ? 1 : 0);
    formData.append("tipo_item", parseInt(tipoItem));
    formData.append("unidad_medida", parseInt(unidadMedida));
    formData.append("descripcion", descripcion || "");
    formData.append("tipo_producto", "PRODUCTO");
    formData.append("categoria", categoria); // 🆕
    if (fotoFile) formData.append("foto", fotoFile); // 🆕
    if (quitarFotoExistente) formData.append("quitar_foto", 1); // 🆕
    if (tienePresentaciones) {
      formData.append("presentations", JSON.stringify(presentations.map(p => ({
        descripcion: p.descripcion,
        precio_compra: tipoPresentacion === 'variante' ? parseFloat(p.precio_compra) : null,
        precio_venta: parseFloat(p.precio_venta),
        factor_conversion: tipoPresentacion === 'empaque' ? parseInt(p.factor_conversion) : 1,
        tipo_presentacion: tipoPresentacion,
        stock_independiente: tipoPresentacion === 'variante' ? parseInt(p.stock_independiente) : null
      }))));
    }

    try {
      if (editingProduct) {
        // 🆕 Laravel necesita _method=PUT para FormData
        formData.append("_method", "PUT");
        await api.post(`/products/${editingProduct.id}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        Swal.fire("Actualizado", "Producto actualizado correctamente", "success");
      } else {
        await api.post("/products", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        Swal.fire("Guardado", "Producto registrado correctamente", "success");
      }

      resetForm();
      if (typeof onSaved === "function") {
        onSaved();
      }
    } catch (error) {
      console.error("ERROR REAL:", error);

      Swal.fire(
        "Error",
        error.response?.data?.message || "Error al guardar el producto",
        "error"
      );
    }
  };

  // Determinar si los campos de stock y precio_compra deben estar deshabilitados
  const esServicio = parseInt(tipoItem) === 2;

  // Calcular margen de ganancia en tiempo real (solo si NO tiene presentaciones)
  const calcularMargen = () => {
    if (tienePresentaciones || !precioCompra || !precioVenta || esServicio) return null;
    const compra = parseFloat(precioCompra);
    const venta = parseFloat(precioVenta);
    if (compra === 0) return null;
    return (((venta - compra) / compra) * 100).toFixed(2);
  };

  const margen = calcularMargen();

  return (
    <div className="card" style={{ marginBottom: "24px" }}>
      <div className="card-header">
        <div className="d-flex align-items-center gap-2">
          <Package size={24} color="#667eea" />
          <h2 className="card-title" style={{ fontSize: "20px" }}>
            {editingProduct ? "Editar Producto/Servicio" : "Nuevo Producto/Servicio"}
          </h2>
        </div>
      </div>

      <div className="card-body">
        <form onSubmit={handleSubmit}>
          {/* INFORMACIÓN BÁSICA */}
          <h4
            style={{
              fontSize: "16px",
              fontWeight: 600,
              marginBottom: "16px",
              color: "#2d3748",
            }}
          >
            Información Básica
          </h4>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "200px 1fr",
              gap: "16px",
              marginBottom: "16px",
            }}
          >
            <div className="form-group">
<label className="form-label">Código</label>
<input
  type="text"
  className="form-control"
  placeholder="Escanear o escribir (opcional)"
  value={codigo}
  onChange={(e) => setCodigo(e.target.value)}
  ref={codigoRef}
  autoFocus
/>
            </div>

            <div className="form-group">
              <label className="form-label">Nombre del Producto/Servicio *</label>
              <input
                type="text"
                className="form-control"
                placeholder="Ingrese el nombre"
                required
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
              />
            </div>
          </div>

          {/* 🆕 CATEGORÍA Y FOTO */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "16px",
              marginBottom: "24px",
            }}
          >
            {/* CATEGORÍA */}
            <div className="form-group">
              <label className="form-label">Categoría *</label>
              <select
                className="form-control"
                value={categoria}
                onChange={(e) => setCategoria(e.target.value)}
                required
              >
{categorias.map((cat) => (
  <option key={cat.value} value={cat.value}>
    {cat.label}
  </option>
))}
              </select>
              <small style={{ color: "#718096", fontSize: "12px" }}>
                Sección donde aparecerá en el menú
              </small>
            </div>

            {/* FOTO */}
            <div className="form-group">
              <label className="form-label">
                <Image size={14} style={{ display: "inline", marginRight: "4px" }} />
                Foto del producto
              </label>

              {fotoPreview ? (
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <img
                    src={fotoPreview}
                    alt="Preview"
                    style={{
                      width: "72px",
                      height: "72px",
                      objectFit: "cover",
                      borderRadius: "8px",
                      border: "2px solid #e2e8f0",
                    }}
                  />
                  <div>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => fotoInputRef.current?.click()}
                      style={{ display: "block", marginBottom: "6px" }}
                    >
                      Cambiar foto
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={quitarFoto}
                    >
                      Quitar foto
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    border: "2px dashed #cbd5e0",
                    borderRadius: "8px",
                    padding: "16px",
                    textAlign: "center",
                    cursor: "pointer",
                    background: "#fafafa",
                  }}
                  onClick={() => fotoInputRef.current?.click()}
                >
                  <Image size={28} color="#a0aec0" style={{ marginBottom: "6px" }} />
                  <p style={{ margin: 0, color: "#718096", fontSize: "13px" }}>
                    Click para subir foto
                  </p>
                  <p style={{ margin: "2px 0 0", color: "#a0aec0", fontSize: "11px" }}>
                    JPG, PNG — máx. 2MB
                  </p>
                </div>
              )}

              <input
                ref={fotoInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleFotoChange}
              />
            </div>
          </div>

          {/* TIPO DE ÍTEM Y UNIDAD DE MEDIDA */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "16px",
              marginBottom: "24px",
            }}
          >
            <div className="form-group">
              <label className="form-label">
                <Tag size={14} style={{ display: "inline", marginRight: "4px" }} />
                Tipo de Ítem *
              </label>
              <select
                className="form-control"
                value={tipoItem}
                onChange={(e) => {
                  setTipoItem(e.target.value);
                  if (e.target.value === "2") {
                    setTienePresentaciones(false);
                    setPresentations([]);
                  }
                }}
                required
              >
                <option value={1}>Bien</option>
                <option value={2}>Servicio</option>
              </select>
              <small style={{ color: "#718096", fontSize: "12px" }}>
                {esServicio ? "Los servicios no manejan inventario ni costo de compra" : "Los bienes manejan inventario y costos"}
              </small>
            </div>

            <div className="form-group">
              <label className="form-label">
                <Ruler size={14} style={{ display: "inline", marginRight: "4px" }} />
                Unidad de Medida *
              </label>
              <select
                className="form-control"
                value={unidadMedida}
                onChange={(e) => setUnidadMedida(e.target.value)}
                required
              >
                {UNIDADES_MEDIDA.map((unidad) => (
                  <option key={unidad.codigo} value={unidad.codigo}>
                    {unidad.nombre}
                  </option>
                ))}
              </select>
              <small style={{ color: "#718096", fontSize: "12px" }}>
                Según catálogo de Hacienda (CAT-014)
              </small>
            </div>
          </div>

          {/* 🆕 CHECKBOX DE PRESENTACIONES MÚLTIPLES + TIPO */}
          {!esServicio && (
            <div
              style={{
                background: "#f0f9ff",
                padding: "16px",
                borderRadius: "10px",
                marginBottom: "24px",
                border: "2px solid #0ea5e9"
              }}
            >
              <div className="form-check" style={{ marginBottom: "12px" }}>
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="tienePresentaciones"
                  checked={tienePresentaciones}
                  onChange={(e) => {
                    setTienePresentaciones(e.target.checked);
                    if (!e.target.checked) {
                      setPresentations([]);
                      setTipoPresentacion("empaque");
                    }
                  }}
                  style={{ cursor: "pointer" }}
                />
                <label
                  className="form-check-label"
                  htmlFor="tienePresentaciones"
                  style={{ cursor: "pointer", fontWeight: 600, color: "#0369a1" }}
                >
                  📦 ¿Este producto tiene múltiples presentaciones?
                </label>
                <small
                  style={{
                    display: "block",
                    color: "#0369a1",
                    fontSize: "12px",
                    marginTop: "4px",
                    marginLeft: "24px",
                  }}
                >
                  Ejemplo: Coca Cola en unidad, 12-pack o 24-pack con precios diferentes
                </small>
              </div>

              {/* 🆕 RADIO BUTTONS PARA TIPO DE PRESENTACIÓN */}
              {tienePresentaciones && (
                <div style={{ 
                  marginTop: "16px", 
                  paddingTop: "16px", 
                  borderTop: "1px solid #0ea5e9" 
                }}>
                  <label style={{ 
                    display: "block", 
                    fontWeight: 600, 
                    color: "#0369a1", 
                    marginBottom: "12px" 
                  }}>
                    ¿Qué tipo de presentaciones maneja?
                  </label>

                  <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                    {/* OPCIÓN: EMPAQUE */}
                    <div 
                      style={{
                        background: tipoPresentacion === 'empaque' ? '#e0f2fe' : 'white',
                        padding: "12px",
                        borderRadius: "8px",
                        border: tipoPresentacion === 'empaque' ? '2px solid #0284c7' : '2px solid #cbd5e0',
                        cursor: "pointer"
                      }}
                      onClick={() => {
                        setTipoPresentacion('empaque');
                        setPresentations([]);
                      }}
                    >
                      <div className="form-check">
                        <input
                          type="radio"
                          className="form-check-input"
                          id="tipoEmpaque"
                          name="tipoPresentacion"
                          value="empaque"
                          checked={tipoPresentacion === 'empaque'}
                          onChange={(e) => {
                            setTipoPresentacion(e.target.value);
                            setPresentations([]);
                          }}
                          style={{ cursor: "pointer" }}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="tipoEmpaque"
                          style={{ cursor: "pointer", fontWeight: 500 }}
                        >
                          📦 Por empaque/cantidad (stock compartido)
                        </label>
                        <small
                          style={{
                            display: "block",
                            color: "#0369a1",
                            fontSize: "11px",
                            marginTop: "4px",
                            marginLeft: "24px",
                          }}
                        >
                          Ejemplo: Unidad, 6-pack, 12-pack, 24-pack. Todas usan el mismo inventario.
                        </small>
                      </div>
                    </div>

                    {/* OPCIÓN: VARIANTE */}
                    <div 
                      style={{
                        background: tipoPresentacion === 'variante' ? '#e0f2fe' : 'white',
                        padding: "12px",
                        borderRadius: "8px",
                        border: tipoPresentacion === 'variante' ? '2px solid #0284c7' : '2px solid #cbd5e0',
                        cursor: "pointer"
                      }}
                      onClick={() => {
                        setTipoPresentacion('variante');
                        setPresentations([]);
                      }}
                    >
                      <div className="form-check">
                        <input
                          type="radio"
                          className="form-check-input"
                          id="tipoVariante"
                          name="tipoPresentacion"
                          value="variante"
                          checked={tipoPresentacion === 'variante'}
                          onChange={(e) => {
                            setTipoPresentacion(e.target.value);
                            setPresentations([]);
                          }}
                          style={{ cursor: "pointer" }}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="tipoVariante"
                          style={{ cursor: "pointer", fontWeight: 500 }}
                        >
                          🏷️ Por variante/sabor (stock independiente)
                        </label>
                        <small
                          style={{
                            display: "block",
                            color: "#0369a1",
                            fontSize: "11px",
                            marginTop: "4px",
                            marginLeft: "24px",
                          }}
                        >
                          Ejemplo: Pollo, Camarón, Res. Cada sabor tiene su propio inventario y código de barras.
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* SEPARADOR */}
          <div
            style={{
              borderTop: "2px solid #e2e8f0",
              margin: "24px 0",
            }}
          ></div>

          {/* PRECIOS E INVENTARIO */}
          <h4
            style={{
              fontSize: "16px",
              fontWeight: 600,
              marginBottom: "16px",
              color: "#2d3748",
            }}
          >
            Precios e Inventario
          </h4>

          {/* SI TIENE PRESENTACIONES, MOSTRAR TABLA */}
          {tienePresentaciones ? (
            <div style={{ marginBottom: "24px" }}>
              <div style={{ 
                display: "flex", 
                justifyContent: "space-between", 
                alignItems: "center",
                marginBottom: "12px"
              }}>
                <label className="form-label" style={{ margin: 0 }}>
                  Presentaciones del Producto
                </label>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={agregarPresentacion}
                >
                  <Plus size={16} />
                  Agregar Presentación
                </button>
              </div>

              {presentations.length === 0 ? (
                <div style={{
                  padding: "32px",
                  textAlign: "center",
                  background: "#f7fafc",
                  borderRadius: "10px",
                  border: "2px dashed #cbd5e0"
                }}>
                  <p style={{ color: "#718096", margin: 0 }}>
                    No hay presentaciones agregadas. Haz clic en "Agregar Presentación" para comenzar.
                  </p>
                </div>
              ) : (
                <div className="table-container" style={{ overflowX: 'auto' }}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th style={{ width: "20%" }}>Descripción *</th>
                        {/* 🆕 Precio de compra solo para variantes */}
                        {tipoPresentacion === 'variante' && (
                          <th style={{ width: "15%" }}>Precio Compra *</th>
                        )}
                        <th style={{ width: "15%" }}>Precio Venta *</th>
                        {/* Columna condicional según el tipo */}
                        {tipoPresentacion === 'empaque' ? (
                          <th style={{ width: "15%" }}>Factor Conversión *</th>
                        ) : (
                          <th style={{ width: "15%" }}>Stock *</th>
                        )}
                        <th style={{ width: "10%", textAlign: "center" }}>Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {presentations.map((pres, index) => (
                        <tr key={index}>
                          <td>
                            <input
                              type="text"
                              className="form-control"
                              placeholder={tipoPresentacion === 'empaque' ? 'Ej: "Unidad", "12-Pack"' : 'Ej: "Pollo", "Camarón"'}
                              value={pres.descripcion}
                              onChange={(e) => actualizarPresentacion(index, 'descripcion', e.target.value)}
                              required
                            />
                          </td>
                          {/* 🆕 Campo de precio de compra solo para variantes */}
                          {tipoPresentacion === 'variante' && (
                            <td>
                              <input
                                type="number"
                                className="form-control"
                                placeholder="0.00"
                                step="0.01"
                                min="0"
                                value={pres.precio_compra || ''}
                                onChange={(e) => actualizarPresentacion(index, 'precio_compra', e.target.value)}
                                required
                              />
                              <small style={{ color: "#718096", fontSize: "11px" }}>
                                Costo
                              </small>
                            </td>
                          )}
                          <td>
                            <input
                              type="number"
                              className="form-control"
                              placeholder="0.00"
                              step="0.01"
                              min="0"
                              value={pres.precio_venta}
                              onChange={(e) => actualizarPresentacion(index, 'precio_venta', e.target.value)}
                              required
                            />
                          </td>
                          {/* Campo condicional según el tipo */}
                          {tipoPresentacion === 'empaque' ? (
                            <td>
                              <input
                                type="number"
                                className="form-control"
                                placeholder="1"
                                min="1"
                                value={pres.factor_conversion}
                                onChange={(e) => actualizarPresentacion(index, 'factor_conversion', e.target.value)}
                                required
                              />
                              <small style={{ color: "#718096", fontSize: "11px" }}>
                                Unidades base
                              </small>
                            </td>
                          ) : (
                            <td>
                              <input
                                type="number"
                                className="form-control"
                                placeholder="0"
                                min="0"
                                value={pres.stock_independiente}
                                onChange={(e) => actualizarPresentacion(index, 'stock_independiente', e.target.value)}
                                required
                              />
                              <small style={{ color: "#718096", fontSize: "11px" }}>
                                Stock propio
                              </small>
                            </td>
                          )}
                          <td className="text-center">
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              onClick={() => eliminarPresentacion(index)}
                              title="Eliminar presentación"
                            >
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* 🆕 Ayuda contextual según el tipo */}
              <div style={{
                background: "#fffbeb",
                padding: "12px",
                borderRadius: "8px",
                border: "1px solid #fbbf24",
                marginTop: "12px"
              }}>
                <small style={{ color: "#92400e", fontSize: "12px" }}>
                  {tipoPresentacion === 'empaque' ? (
                    <>
                      💡 <strong>Factor de conversión:</strong> Indica cuántas unidades base representa esta presentación. 
                      Por ejemplo, un 12-pack tiene factor 12, un 24-pack tiene factor 24.
                    </>
                  ) : (
                    <>
                      💡 <strong>Stock independiente:</strong> Cada variante maneja su propio inventario. 
                      Por ejemplo, si tienes 50 unidades de Pollo y 30 de Camarón, son stocks separados.
                    </>
                  )}
                </small>
              </div>
            </div>
          ) : (
            /* PRECIOS NORMALES (sin presentaciones) */
            <div
              style={{
                display: "grid",
                gridTemplateColumns: esServicio ? "1fr 1fr 1fr" : "1fr 1fr 1fr 1fr",
                gap: "16px",
                marginBottom: "16px",
              }}
            >
              {/* PRECIO DE COMPRA */}
              {!esServicio && (
                <div className="form-group">
                  <label className="form-label">
                    <ShoppingCart size={14} style={{ display: "inline", marginRight: "4px" }} />
                    Precio de Compra *
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    required={!esServicio}
                    value={precioCompra}
                    onChange={(e) => setPrecioCompra(e.target.value)}
                  />
                  <small style={{ color: "#718096", fontSize: "12px" }}>
                    Costo de adquisición
                  </small>
                </div>
              )}

              {/* PRECIO DE VENTA */}
              <div className="form-group">
                <label className="form-label">
                  <DollarSign size={14} style={{ display: "inline", marginRight: "4px" }} />
                  Precio de Venta *
                </label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  required
                  value={precioVenta}
                  onChange={(e) => setPrecioVenta(e.target.value)}
                />
                <small style={{ color: "#718096", fontSize: "12px" }}>
                  Precio al cliente
                </small>
              </div>

              {/* MARGEN DE GANANCIA */}
              {!esServicio && margen !== null && (
                <div className="form-group">
                  <label className="form-label">Margen</label>
                  <div
                    style={{
                      padding: "8px 12px",
                      backgroundColor: parseFloat(margen) >= 0 ? "#d1fae5" : "#fee2e2",
                      color: parseFloat(margen) >= 0 ? "#065f46" : "#991b1b",
                      borderRadius: "6px",
                      fontWeight: 600,
                      textAlign: "center",
                      fontSize: "16px",
                    }}
                  >
                    {margen}%
                  </div>
                  <small style={{ color: "#718096", fontSize: "12px" }}>
                    Ganancia estimada
                  </small>
                </div>
              )}

              {/* STOCK ACTUAL */}
              <div className="form-group">
                <label className="form-label">
                  <Boxes size={14} style={{ display: "inline", marginRight: "4px" }} />
                  Stock Actual *
                </label>
                <input
                  type="number"
                  className="form-control"
                  placeholder={esServicio ? "-" : "0"}
                  min="0"
                  required
                  value={esServicio ? "" : stock}
                  onChange={(e) => setStock(e.target.value)}
                  disabled={esServicio}
                  style={{
                    backgroundColor: esServicio ? "#f7fafc" : "white",
                    color: esServicio ? "#a0aec0" : "inherit",
                  }}
                />
                {esServicio && (
                  <small style={{ color: "#718096", fontSize: "12px" }}>
                    No aplica
                  </small>
                )}
              </div>

              {/* STOCK MÍNIMO */}
              <div className="form-group">
                <label className="form-label">
                  <AlertCircle size={14} style={{ display: "inline", marginRight: "4px" }} />
                  Stock Mínimo
                </label>
                <input
                  type="number"
                  className="form-control"
                  placeholder={esServicio ? "-" : "5"}
                  min="0"
                  value={esServicio ? "" : stockMinimo}
                  onChange={(e) => setStockMinimo(e.target.value)}
                  disabled={esServicio}
                  style={{
                    backgroundColor: esServicio ? "#f7fafc" : "white",
                    color: esServicio ? "#a0aec0" : "inherit",
                  }}
                />
                {!esServicio && (
                  <small style={{ color: "#718096", fontSize: "12px" }}>
                    Alerta de reposición
                  </small>
                )}
              </div>
            </div>
          )}

          {/* INFORMACIÓN ADICIONAL: Precio de compra y stock (solo para EMPAQUE) */}
          {!esServicio && tienePresentaciones && tipoPresentacion === 'empaque' && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: "16px",
                marginBottom: "16px",
              }}
            >
              <div className="form-group">
                <label className="form-label">
                  <ShoppingCart size={14} style={{ display: "inline", marginRight: "4px" }} />
                  Precio de Compra (Unitario) *
                </label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  required
                  value={precioCompra}
                  onChange={(e) => setPrecioCompra(e.target.value)}
                />
                <small style={{ color: "#718096", fontSize: "12px" }}>
                  Costo por unidad base
                </small>
              </div>

              <div className="form-group">
                <label className="form-label">
                  <Boxes size={14} style={{ display: "inline", marginRight: "4px" }} />
                  Stock Actual *
                </label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="0"
                  min="0"
                  required
                  value={stock}
                  onChange={(e) => setStock(e.target.value)}
                />
                <small style={{ color: "#718096", fontSize: "12px" }}>
                  Unidades en inventario
                </small>
              </div>

              <div className="form-group">
                <label className="form-label">
                  <AlertCircle size={14} style={{ display: "inline", marginRight: "4px" }} />
                  Stock Mínimo
                </label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="5"
                  min="0"
                  value={stockMinimo}
                  onChange={(e) => setStockMinimo(e.target.value)}
                />
                <small style={{ color: "#718096", fontSize: "12px" }}>
                  Alerta de reposición
                </small>
              </div>
            </div>
          )}

          {/* SEPARADOR */}
          <div
            style={{
              borderTop: "2px solid #e2e8f0",
              margin: "24px 0",
            }}
          ></div>

          {/* DESCRIPCIÓN */}
          <div className="form-group" style={{ marginBottom: "24px" }}>
            <label className="form-label">Descripción (opcional)</label>
            <input
              type="text"
              className="form-control"
              placeholder="Descripción adicional"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
            />
          </div>

          {/* CHECKBOX IVA */}
          <div
            style={{
              background: "#f7fafc",
              padding: "16px",
              borderRadius: "10px",
              marginBottom: "24px",
            }}
          >
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="exentoIva"
                checked={exentoIva}
                onChange={(e) => setExentoIva(e.target.checked)}
                style={{ cursor: "pointer" }}
              />
              <label
                className="form-check-label"
                htmlFor="exentoIva"
                style={{ cursor: "pointer", fontWeight: 500 }}
              >
                {esServicio ? "Servicio" : "Producto"} exento de IVA
              </label>
              <small
                style={{
                  display: "block",
                  color: "#718096",
                  fontSize: "12px",
                  marginTop: "4px",
                  marginLeft: "24px",
                }}
              >
                Marque esta opción si no aplica IVA
              </small>
            </div>
          </div>

          {/* BOTONES */}
          <div className="d-flex gap-2">
            <button type="submit" className="btn btn-success">
              <Save size={18} />
              {editingProduct ? "Actualizar" : "Guardar"}
            </button>

            <button type="button" className="btn btn-secondary" onClick={onCancel}>
              <X size={18} />
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}